"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";
import { BRAND, EASE } from "../../constants";
import { DotBadge } from "../../primitives";
import { JOURNEY, MAYA } from "./data";

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 px-2.5 py-1">
      <span className="text-[13px] text-muted-foreground">{label}</span>
      <span className="text-[13px] font-medium text-foreground">{value}</span>
    </div>
  );
}

function MembershipStat({ unlimited }: { unlimited: boolean }) {
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={unlimited ? "unlimited" : "base"}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.25, ease: EASE }}
      >
        <DotBadge dot={unlimited ? "bg-primary dark:bg-[hsl(var(--brand-accent))]" : "bg-success"}>
          {unlimited ? "Unlimited" : "4× per month"}
        </DotBadge>
      </motion.span>
    </AnimatePresence>
  );
}

function LastVisit({ returned }: { returned: boolean }) {
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={returned ? "today" : "stale"}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.25, ease: EASE }}
        className={cn(
          "text-[13px] font-medium tabular-nums",
          returned ? "text-success" : "text-amber-600 dark:text-amber-400",
        )}
      >
        {returned ? "Today" : "23 days ago"}
      </motion.span>
    </AnimatePresence>
  );
}

function JourneyStepper({ activeIndex, reduce }: { activeIndex: number; reduce: boolean }) {
  return (
    <ol className="relative">
      <span className="pointer-events-none absolute bottom-4 left-[9px] top-4 w-px bg-border" />
      {JOURNEY.map((step, i) => {
        const done = i < activeIndex;
        const current = i === activeIndex;
        return (
          <li key={step.id} className="relative flex items-center py-1.5 pl-7">
            <motion.span
              className={cn(
                "absolute left-0 flex h-5 w-5 items-center justify-center rounded-full border transition-colors duration-300",
                done
                  ? "border-transparent bg-success"
                  : current
                    ? "border-transparent bg-background"
                    : "border-border bg-muted",
              )}
              animate={
                reduce
                  ? undefined
                  : {
                      boxShadow: current
                        ? "0 0 0 3px hsl(var(--muted-foreground) / 0.25)"
                        : "0 0 0 0 hsl(var(--muted-foreground) / 0)",
                    }
              }
              transition={{ duration: 0.3 }}
            >
              {done ? (
                <Check className="h-3 w-3 text-success-foreground" />
              ) : (
                <span
                  className={cn(
                    "text-xs font-medium tabular-nums",
                    current ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {i + 1}
                </span>
              )}
            </motion.span>
            <span
              className={cn(
                "truncate text-[13px] font-medium transition-colors",
                done || current ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function MemberPanel({
  activeIndex,
  unlimited,
  returned,
  reduce,
}: {
  activeIndex: number;
  unlimited: boolean;
  returned: boolean;
  reduce: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      {/* Identity card — mirrors the contact sidebar's profile block */}
      <div className="rounded-md border border-border bg-background">
        <div className="flex items-center gap-2.5 p-2">
          <Avatar name={MAYA.name} src={MAYA.src} size="md" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{MAYA.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {BRAND} · {MAYA.location}
            </p>
          </div>
        </div>
        <div className="divide-y divide-border border-t border-border">
          <Row label="Membership" value={<MembershipStat unlimited={unlimited} />} />
          <Row label="Member since" value={MAYA.since} />
          <Row label="Last visit" value={<LastVisit returned={returned} />} />
        </div>
      </div>

      {/* Journey card — the playbook milestones for this member */}
      <div className="rounded-md border border-border bg-background">
        <div className="flex items-center gap-1.5 border-b border-border px-2.5 py-1.5">
          <span className="text-[13px] font-medium text-foreground">Journey</span>
          <Badge variant="outline" className="ml-auto bg-background text-[10px]">
            Step {Math.min(activeIndex + 1, JOURNEY.length)} of {JOURNEY.length}
          </Badge>
        </div>
        <div className="px-2.5 py-1.5">
          <JourneyStepper activeIndex={activeIndex} reduce={reduce} />
        </div>
      </div>
    </div>
  );
}
